/**
 * Client for the `workspace` edge function. No component talks to the
 * `workspaces` table directly any more: creating one goes through the plan's cap
 * and the duplicate-name rule on the server, and deleting one through the checks
 * that keep it from throwing away money or leaving calls running. Field names
 * are the column names. Keep in sync with `supabase/functions/workspace/` and
 * `utils/workspace-rules.ts`.
 */

import { api } from "@/lib/api-client";

const BASE = "/workspace";

/** The caller's role, as `get_workspace_role()` decides it: owning wins over a membership. */
export type WorkspaceRole = "owner" | "admin" | "developer" | "member";

/** A workspace as the function returns it: no billing column reaches the browser this way. */
export interface Workspace {
  id: string;
  name: string;
  owner_id: string;
  created_at: string | null;
  updated_at: string | null;
  role: WorkspaceRole;
}

/** How many workspaces the user owns, and how many their plan allows. */
export interface WorkspaceAllowance {
  owned: number;
  allowed: number;
}

/** Why a workspace cannot be deleted yet. `message` is the sentence to show; null when nothing blocks. */
export interface DeleteCheck {
  reasons: string[];
  message: string | null;
}

/** Every workspace the user owns or belongs to, newest first. */
export const listWorkspaces = () => api.postList<Workspace>(`${BASE}/list`);

/** Checked before offering a create, so one the plan would refuse is never sent. */
export const getWorkspaceAllowance = () => api.getItem<WorkspaceAllowance>(`${BASE}/allowance`);

/** What would stop a delete right now (owner only), shown before the owner confirms. */
export const checkWorkspaceDelete = (id: string) => api.getItem<DeleteCheck>(`${BASE}/${id}/delete-check`);

/** One workspace, with the user's role in it. A 404 is "no access" as well as "deleted". */
export const getWorkspace = (id: string) => api.getItem<Workspace>(`${BASE}/${id}`);

/** Refused with 403 when the plan allows no more (Pro would), 409 at Pro's cap or for a name already used. */
export const createWorkspace = (name: string) => api.post<Workspace>(BASE, { name });

export const renameWorkspace = (id: string, name: string) => api.put<Workspace>(`${BASE}/${id}`, { name });

/** Refused with 409, every reason in one sentence, while it would lose money or leave something running. */
export const deleteWorkspace = (id: string) => api.del(`${BASE}/${id}`);
